import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import { fmtIST } from "@/shared/lib/time";
import {
  Eye, CheckCircle2, Clock, Send, MessageSquareWarning, FileText, Sparkles,
  ArrowLeft, ChevronRight, FolderOpen, MessageSquare, Paperclip, Lock, User, Image as ImageIcon, Layers,
} from "lucide-react";
import { toast } from "sonner";

/* CSM Design Review board (Msg 905) — designer → CSM → client gate.
   Project-grouped master-detail: the CSM reviews each in-house draft and either
   forwards it to the client (with a comment) or sends it back to the designer
   for changes. Mirrors the Designer Portal so it scales to many line items. */

const STATE_META = {
  csm_review:       { label: "Review needed",    dot: "bg-amber-400",   chip: "bg-amber-100 text-amber-800 border-amber-300",      icon: Eye },
  client_changes:   { label: "Client changes",   dot: "bg-rose-400",    chip: "bg-rose-100 text-rose-700 border-rose-300",         icon: MessageSquareWarning },
  with_client:      { label: "With client",      dot: "bg-blue-400",    chip: "bg-blue-100 text-blue-700 border-blue-300",         icon: Clock },
  awaiting_designer:{ label: "Awaiting designer", dot: "bg-zinc-300",   chip: "bg-zinc-100 text-zinc-600 border-zinc-200",         icon: Clock },
  approved:         { label: "Approved",         dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
};
const isImg = (u) => /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(u || "");

export default function DesignReview() {
  const [projects, setProjects] = useState(null);
  const [view, setView] = useState({ code: null, bid: null });

  const load = useCallback(() => {
    api.get("/design-review")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = view.code ? (projects || []).find((p) => p.project_code === view.code) : null;
  const openProject = (code) => {
    const p = (projects || []).find((x) => x.project_code === code);
    const first = p?.lines.find((l) => l.actionable) || p?.lines[0];
    setView({ code, bid: first?.boq_id || null });
  };

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="design-review-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} selectedBid={view.bid}
                     onSelect={(bid) => setView({ code: active.project_code, bid })}
                     onBack={() => setView({ code: null, bid: null })} onDone={load} />
      ) : (
        <ProjectList projects={projects} onOpen={openProject} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  const totals = projects.reduce((a, p) => ({
    review: a.review + p.review_count, changes: a.changes + p.client_changes_count,
  }), { review: 0, changes: 0 });
  return (
    <>
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold">Client Success</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Design Review</h1>
        <p className="text-zinc-500 mt-2 text-sm sm:text-base max-w-2xl">
          Review the designs your team submits, forward the good ones to the client, and send back the rest with clear instructions.
        </p>
        {projects.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Stat label="Awaiting your review" value={totals.review} tone="amber" />
            <Stat label="Client changes to relay" value={totals.changes} tone="rose" />
            <Stat label="Projects" value={projects.length} tone="zinc" />
          </div>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="py-24 text-center" data-testid="dr-empty">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-50 mb-4">
            <Sparkles className="w-7 h-7 text-orange-400" strokeWidth={1.5} />
          </div>
          <div className="font-display text-xl font-bold">Nothing to review</div>
          <p className="text-zinc-500 mt-1 text-sm">Designs your team submits will show up here for your review.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)} data-testid={`dr-project-card-${p.project_code}`}
                    className="group text-left bg-white border border-zinc-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-orange-300 transition-all">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-orange-600">
                  <FolderOpen className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-orange-500 transition-colors" strokeWidth={1.5} />
              </div>
              <div className="font-display text-lg font-bold tracking-tight mt-4 truncate">{p.project_name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400"><ProjRef code={p.project_code} /></div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.review_count > 0 && <Pill tone="amber">{p.review_count} to review</Pill>}
                {p.client_changes_count > 0 && <Pill tone="rose">{p.client_changes_count} client changes</Pill>}
                <Pill tone="zinc">{p.total} line{p.total !== 1 ? "s" : ""}</Pill>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function ProjectView({ project, selectedBid, onSelect, onBack, onDone }) {
  const selected = project.lines.find((l) => l.boq_id === selectedBid) || project.lines[0];

  // Group the lines by sub-scope so the rail reads "Reception walls → Wall 1, Wall 2…"
  // instead of a long flat list. (Msg 928)
  const groups = useMemo(() => {
    const m = new Map();
    for (const l of project.lines) {
      const g = l.group || "";
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(l);
    }
    return [...m.entries()]
      .sort((a, b) => (a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0])))
      .map(([name, lines]) => ({ name, lines }));
  }, [project.lines]);

  return (
    <div data-testid={`dr-project-view-${project.project_code}`}>
      <button onClick={onBack} data-testid="dr-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-orange-600 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All projects
      </button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold"><ProjRef code={project.project_code} /></div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.project_name}</h1>
          <p className="text-zinc-500 mt-1 text-sm">{project.lines.length} design line item{project.lines.length !== 1 ? "s" : ""} · pick one to review.</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-[minmax(280px,340px)_1fr] gap-6 items-start">
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden lg:sticky lg:top-20 lg:max-h-[calc(100vh-6.5rem)] lg:overflow-y-auto" data-testid="dr-line-rail">
          {groups.map((g) => (
            <div key={g.name || "__ungrouped__"} data-testid={`dr-group-${g.name || "ungrouped"}`}>
              <div className="flex items-center gap-2 px-3.5 py-2 bg-zinc-50/80 border-b border-zinc-100">
                <Layers className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 font-semibold truncate">
                  {g.name || "Ungrouped"}
                </span>
                <span className="text-[10px] text-zinc-400 flex-shrink-0">· {g.lines.length}</span>
              </div>
              <div className="divide-y divide-zinc-100">
                {g.lines.map((l) => (
                  <LineRow key={l.boq_id} l={l} active={l.boq_id === selected?.boq_id} onClick={() => onSelect(l.boq_id)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        {selected && (
          <div className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6.5rem)] lg:overflow-y-auto rounded-2xl">
            <LineDetail key={selected.boq_id} code={project.project_code} line={selected} onDone={onDone} />
          </div>
        )}
      </div>
    </div>
  );
}

function LineRow({ l, active, onClick }) {
  const meta = STATE_META[l.state] || STATE_META.awaiting_designer;
  const thumb = l.current_design?.file_url;
  return (
    <button onClick={onClick} data-testid={`dr-line-row-${l.boq_id}`}
            className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors ${active ? "bg-orange-50/70" : "hover:bg-zinc-50"}`}>
      <div className="w-10 h-10 rounded-lg bg-zinc-50 border border-zinc-200 overflow-hidden flex items-center justify-center flex-shrink-0">
        {thumb && isImg(thumb)
          ? <img src={thumb} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          : <ImageIcon className="w-4 h-4 text-zinc-300" strokeWidth={1.5} />}
      </div>
      <div className="min-w-0 flex-1">
        {l.wall_label && <div className="font-mono text-[10px] uppercase tracking-wider text-orange-500">{l.wall_label}</div>}
        <div className="text-sm font-medium text-zinc-900 truncate">
          {l.title}
          {l.linked_count > 1 && (
            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold whitespace-nowrap"
                  data-testid={`dr-linked-badge-${l.boq_id}`}>1 design × {l.linked_count}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          <span className="text-[11px] text-zinc-500">{meta.label}</span>
        </div>
      </div>
      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${active ? "text-orange-500" : "text-zinc-300"}`} strokeWidth={1.5} />
    </button>
  );
}

function LineDetail({ code, line, onDone }) {
  const meta = STATE_META[line.state] || STATE_META.awaiting_designer;
  const Icon = meta.icon;
  const d = line.current_design;
  const detailRef = useRef(null);
  // On small screens the detail renders below the list — bring it into view.
  useEffect(() => {
    if (window.innerWidth < 1024 && detailRef.current) {
      const r = detailRef.current.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.5) {
        detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [line.boq_id]);
  const [comment, setComment] = useState("");
  const [instr, setInstr] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [mode, setMode] = useState(null);   // null | 'send' | 'changes'
  const [saving, setSaving] = useState(false);

  async function sendToClient() {
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${line.boq_id}/design/send-to-client`, { client_comment: comment.trim() });
      toast.success("Sent to the client for approval."); setMode(null); setComment(""); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }
  async function requestChanges() {
    if (!instr.trim()) { toast.error("Write the instruction for the designer"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${line.boq_id}/design/request-changes`,
                     { instruction: instr.trim(), file_url: fileUrl, file_name: fileName });
      toast.success("Sent back to the designer."); setMode(null); setInstr(""); setFileUrl(""); setFileName(""); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div ref={detailRef} className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`dr-detail-${line.boq_id}`}>
      <div className="p-5 sm:p-6 border-b border-zinc-100">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {line.wall_label && <div className="font-mono text-[11px] uppercase tracking-widest text-orange-500">{line.wall_label}</div>}
            <h3 className="font-display text-xl font-bold tracking-tight mt-0.5">{line.title}</h3>
            {line.linked_count > 1 && (
              <div className="text-[11px] text-indigo-600 font-semibold mt-0.5" data-testid={`dr-linked-note-${line.boq_id}`}>
                Shared-design set — one review here approves all {line.linked_count} linked items
              </div>
            )}
            {line.designer_name && (
              <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
                <User className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} /> {line.designer_name}
              </div>
            )}
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-medium border whitespace-nowrap ${meta.chip}`}
                data-testid={`dr-status-${line.boq_id}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {meta.label}
          </span>
        </div>

        {line.designer_brief && (
          <div className="mt-4 bg-zinc-50 border border-zinc-100 rounded-xl p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" strokeWidth={1.5} /> Brief you gave
            </div>
            <p className="text-sm text-zinc-700 whitespace-pre-line">{line.designer_brief}</p>
          </div>
        )}
      </div>

      {/* The submitted design */}
      {d ? (
        <div className="p-5 sm:p-6 border-b border-zinc-100">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Submitted design · V{d.version_number}</div>
          <a href={d.file_url} target="_blank" rel="noreferrer"
             className="block rounded-xl border border-zinc-200 overflow-hidden bg-zinc-50 hover:ring-2 hover:ring-orange-300 transition"
             data-testid={`dr-design-preview-${line.boq_id}`}>
            {isImg(d.file_url)
              ? <img src={d.file_url} alt="design" className="w-full max-h-80 object-contain bg-white" onError={(e) => { e.target.style.display = "none"; }} />
              : <div className="flex items-center gap-2 p-4 text-sm text-zinc-600"><FileText className="w-5 h-5 text-zinc-400" strokeWidth={1.5} /> {d.file_name}</div>}
          </a>
          <div className="text-[11px] text-zinc-400 mt-1.5">{d.file_name} · {fmtIST(d.uploaded_at)}</div>
          {d.designer_note && (
            <div className="mt-2 text-sm text-zinc-600 bg-zinc-50 border border-zinc-100 rounded-lg p-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Designer note (internal): </span>{d.designer_note}
            </div>
          )}
        </div>
      ) : (
        <div className="p-5 sm:p-6 border-b border-zinc-100 text-sm text-zinc-400 italic">No design submitted yet — waiting on the designer.</div>
      )}

      {/* Client feedback (when client asked for changes) */}
      {line.state === "client_changes" && line.client_feedback && (
        <div className="px-5 sm:px-6 py-4 bg-rose-50/50 border-b border-rose-100">
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-600 mb-1">Client feedback (internal)</div>
          <p className="text-sm text-zinc-900">"{line.client_feedback}"</p>
        </div>
      )}

      {/* Instruction history (locked) */}
      {line.instructions?.length > 0 && (
        <div className="px-5 sm:px-6 py-4 border-b border-zinc-100">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Instructions sent to designer</div>
          <div className="space-y-2">
            {line.instructions.map((ins, i) => (
              <div key={ins.id || i} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3" data-testid={`dr-instruction-${line.boq_id}-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">#{i + 1} · {ins.by} · {fmtIST(ins.at)}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-600"><Lock className="w-3 h-3" strokeWidth={1.5} /> locked</span>
                </div>
                <p className="text-sm text-zinc-800 whitespace-pre-line">{ins.text}</p>
                {ins.file_url && (
                  <a href={ins.file_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs text-orange-700 hover:underline">
                    <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} /> {ins.file_name || "reference"}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-5 sm:p-6">
        {line.state === "approved" ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Approved by the client — locked.
          </div>
        ) : line.state === "with_client" ? (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            <Clock className="w-4 h-4" strokeWidth={1.5} /> Sent to the client — awaiting their approval.
          </div>
        ) : line.state === "awaiting_designer" ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
            <Clock className="w-4 h-4" strokeWidth={1.5} /> Waiting on the designer to submit a design.
          </div>
        ) : (
          <>
            {/* csm_review → send or changes;  client_changes → changes only */}
            {mode === null && (
              <div className="grid sm:grid-cols-2 gap-3">
                {line.state === "csm_review" && (
                  <button onClick={() => setMode("send")} data-testid={`dr-open-send-${line.boq_id}`}
                          className="inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-emerald-600 text-white font-medium shadow-sm hover:bg-emerald-700 transition-all hover:-translate-y-0.5">
                    <Send className="w-4 h-4" strokeWidth={1.5} /> Send to client
                  </button>
                )}
                <button onClick={() => setMode("changes")} data-testid={`dr-open-changes-${line.boq_id}`}
                        className={`inline-flex items-center justify-center gap-2 h-12 rounded-xl font-medium transition-all hover:-translate-y-0.5 ${line.state === "csm_review" ? "bg-white border border-zinc-300 text-zinc-700 hover:border-orange-400" : "bg-orange-500 text-white hover:bg-orange-600 sm:col-span-2"}`}>
                  <MessageSquareWarning className="w-4 h-4" strokeWidth={1.5} /> Request changes
                </button>
              </div>
            )}

            {mode === "send" && (
              <div className="space-y-3" data-testid={`dr-send-form-${line.boq_id}`}>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                          placeholder="Optional note shown to the client with this design (e.g. 'Sharing the latest concept for your approval')."
                          className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          data-testid={`dr-client-comment-${line.boq_id}`} />
                <div className="flex gap-2">
                  <button onClick={sendToClient} disabled={saving} data-testid={`dr-send-confirm-${line.boq_id}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">
                    <Send className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Sending…" : "Confirm & send to client"}
                  </button>
                  <button onClick={() => setMode(null)} className="px-4 text-sm text-zinc-500 hover:text-zinc-800">Cancel</button>
                </div>
              </div>
            )}

            {mode === "changes" && (
              <div className="space-y-3" data-testid={`dr-changes-form-${line.boq_id}`}>
                <textarea value={instr} onChange={(e) => setInstr(e.target.value)} rows={3}
                          placeholder="What should the designer change? This is locked once sent and is never shown to the client."
                          className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
                          data-testid={`dr-changes-input-${line.boq_id}`} />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" strokeWidth={1.5} /> Clarity reference (optional)
                  </div>
                  <FileUpload value={fileUrl}
                              onChange={(u) => { setFileUrl(u); setFileName(u ? (u.split("/").pop() || "reference") : ""); }}
                              accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.ai,.cdr,.eps,.zip" testid={`dr-changes-file-${line.boq_id}`} />
                </div>
                <div className="flex gap-2">
                  <button onClick={requestChanges} disabled={saving || !instr.trim()} data-testid={`dr-changes-confirm-${line.boq_id}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-50">
                    <MessageSquareWarning className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Sending…" : "Send back to designer"}
                  </button>
                  <button onClick={() => setMode(null)} className="px-4 text-sm text-zinc-500 hover:text-zinc-800">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border ${cls}`}>
      <span className="font-bold tabular-nums">{value}</span> {label}
    </span>
  );
}
function Pill({ tone, children }) {
  const cls = { amber: "bg-amber-100 text-amber-800", rose: "bg-rose-100 text-rose-700", zinc: "bg-zinc-100 text-zinc-600" }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{children}</span>;
}
